import { Input } from "@/shared/ui/input";
import React, { useMemo } from "react";
import PhoneInput, { isPossiblePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

interface MaskedPhoneInputProps
  extends Omit<React.ComponentProps<typeof Input>, "onChange" | "value"> {
  /** E.164 value, e.g. "+41791234567" (empty string if unset) */
  value: string | undefined;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Default country for formatting/placeholder (ISO 3166-1 alpha-2), defaults to CH */
  defaultCountry?: string;
  /** Show a tiny validation hint using libphonenumber's "possible" check */
  showValidationHint?: boolean;
}

// Adapter so PhoneInput can render our shadcn <Input /> while keeping refs intact
const ShadcnInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>((props, ref) => <Input ref={ref} {...props} />);
ShadcnInput.displayName = "ShadcnInputAdapter";

// Create a minimal synthetic change event carrying just `target.value`
const toSyntheticChange = (
  value: string
): React.ChangeEvent<HTMLInputElement> => {
  return {
    target: { value },
    currentTarget: { value },
  } as React.ChangeEvent<HTMLInputElement>;
};

const MaskedPhoneInput = React.forwardRef<
  HTMLInputElement,
  MaskedPhoneInputProps
>(
  (
    {
      value,
      onChange,
      onBlur,
      placeholder,
      defaultCountry = "CH",
      showValidationHint = true,
      className,
      id,
      name,
      disabled,
      required,
      ...rest
    },
    ref
  ) => {
    const handleChange = React.useCallback(
      (val?: string) => {
        // `val` is already E.164 or undefined
        const next = val ?? "";
        onChange(toSyntheticChange(next));
      },
      [onChange]
    );

    const possible = React.useMemo(
      () => (value ? isPossiblePhoneNumber(value) : true),
      [value]
    );

    return (
      <div className="space-y-1">
        <PhoneInput
          defaultCountry={defaultCountry as any}
          international
          value={value || undefined}
          onChange={handleChange}
          // Render our own Input component
          inputComponent={ShadcnInput as any}
          // Pass native input props down to our Input
          numberInputProps={useMemo(
            () => ({
              id,
              name,
              disabled,
              required,
              inputMode: "tel",
              autoComplete: "tel",
              placeholder:
                placeholder ?? (defaultCountry ? undefined : "Enter phone number"),
              className,
              onBlur: onBlur as any,
              // Spread any remaining safe props intended for the input element
              ...rest,
            }),
            [
              id,
              name,
              disabled,
              required,
              placeholder,
              defaultCountry,
              className,
              onBlur,
              rest,
            ]
          )}
          // Allow container to pick up layout props if needed
          className="w-full"
          ref={ref as any}
        />
        {showValidationHint && value && !possible && (
          <p className="text-xs text-red-600">
            That phone number doesn’t look valid.
          </p>
        )}
      </div>
    );
  }
);

MaskedPhoneInput.displayName = "MaskedPhoneInput";

export { MaskedPhoneInput };
